import { useState } from "react";
import { Button } from "@/common/components/button";
import Stepper from "@/common/components/stepper";
import { InventorySerialSection } from "./InventorySerialSection";
import { IInventoryNode, IPackagingLayer } from "./inventoryTree";
import {
  buildInventorySkeleton,
  globalProgress,
  hasDuplicateSerial,
  hasFormatError,
} from "./serialAccelerators";

const INVENTORY_STEPS = [
  { id: 1, label: "Batch details" },
  { id: 2, label: "Serial entry" },
];

interface IInventoryStep2Props {
  packagingLayers: IPackagingLayer[];
  submitLoading: boolean;
  serverErrorBySerial: Record<string, string>;
  onBack: () => void;
  onSubmit: (tree: IInventoryNode) => void;
}

function setAtPath(node: IInventoryNode, path: number[], patch: Partial<IInventoryNode>): IInventoryNode {
  if (path.length === 0) return { ...node, ...patch };
  const [head, ...rest] = path;
  const children = node.children!.map((child, i) => (i === head ? setAtPath(child, rest, patch) : child));
  return { ...node, children };
}

function footerBlocker(tree: IInventoryNode): string | null {
  const { filled, total } = globalProgress(tree);
  if (filled < total) return `${total - filled} serial${total - filled === 1 ? "" : "s"} remaining`;
  if (hasDuplicateSerial(tree)) return "Fix duplicate serials to continue";
  if (hasFormatError(tree)) return "Fix invalid serial formats to continue";
  return null;
}

export function InventoryStep2({
  packagingLayers,
  submitLoading,
  serverErrorBySerial,
  onBack,
  onSubmit,
}: IInventoryStep2Props) {
  const [tree, setTree] = useState<IInventoryNode>(() => buildInventorySkeleton(packagingLayers));

  function handleChange(path: number[], patch: Partial<IInventoryNode>) {
    setTree((prev) => setAtPath(prev, path, patch));
  }

  const progress = globalProgress(tree);
  const blocker = footerBlocker(tree);
  const percent = progress.total === 0 ? 0 : Math.round((progress.filled / progress.total) * 100);

  return (
    <div className="w-full">
      <div className="mb-6">
        <Stepper steps={INVENTORY_STEPS} currentStep={1} />
      </div>

      <div className="mb-3 flex items-center justify-between">
        <span className="text-sm font-medium capitalize">
          Serials for one complete {tree.packagingType}
        </span>
        <span className="text-xs text-gray-500">
          {progress.filled} of {progress.total} ({percent}%)
        </span>
      </div>
      <div className="mb-3 h-1.5 w-full rounded bg-gray-200">
        <div
          className="h-1.5 rounded bg-green-500 transition-all"
          style={{ width: `${percent}%` }}
        />
      </div>

      <p className="mb-3 text-xs text-gray-500">
        Type a serial and press Enter to jump to the next slot, or use a section's Scan, Range fill, or Paste option below it.
      </p>

      <div className="max-h-[28rem] overflow-y-auto pr-1">
        <InventorySerialSection node={tree} path={[]} onChange={handleChange} serverErrorBySerial={serverErrorBySerial} />
      </div>

      <div className="mt-3 flex items-center justify-between gap-3">
        <Button type="button" secondary onClick={onBack} disabled={submitLoading}>
          Back
        </Button>
        <div className="flex flex-col items-end">
          {blocker && <span className="mb-1 text-xs text-red-600">{blocker}</span>}
          <Button type="button" onClick={() => onSubmit(tree)} disabled={!!blocker || submitLoading}>
            {submitLoading ? "Registering..." : "Register Inventory"}
          </Button>
        </div>
      </div>
    </div>
  );
}
